import React, { useMemo, useState, useCallback } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import {
  AccountCircle,
  AddAlarm,
  Brightness4,
  Brightness7,
  CorporateFare,
  Dashboard,
  Description,
  Fence,
  Home,
  LabelOutlined,
  Logout,
  MenuRounded,
  Person,
  Router,
  Settings,
  Support,
  ExpandMore,
} from "@mui/icons-material";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  useTheme,
  alpha,
  Avatar,
  Box,
  Chip,
  ButtonBase,
  IconButton,
  Autocomplete,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useLabels from "../hooks/useLabels";
import { useActions } from "../hooks/queries/useActions";
import { useDevices } from "../hooks/queries/useDevices";
import { useGeofences } from "../hooks/queries/useGeofences";
import { useContacts } from "../hooks/queries/useContacts";
import { useOrganisations } from "../hooks/queries/useOrganisations";
import { useDashboards } from "../hooks/queries/useDashboards";
import { useReports } from "../hooks/queries/useReports";
import GlobalSearch from "./NavBar/GlobalSearch";
import { OrganisationService } from "../services/OrganisationService";
import { DataService } from "../services/DataService";
import { CONFIG } from "../config";
import { useAuth0 } from "@auth0/auth0-react";

function SidebarLayout({ collapsed, handleCollapsed, handleThemeChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation("common");
  const { user, logout } = useAuth0();
  const [expandedSubMenus, setExpandedSubMenus] = useState({});
  const [openSubMenus, setOpenSubMenus] = useState(() => {
    try {
      const saved = localStorage.getItem("sidebarOpenSubMenus");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const themeMode = useSelector((state) => state.themeMode);
  const orgId = selectedOrganisation?.id;
  const { data: labels = [] } = useLabels(orgId);
  const { data: actions = [] } = useActions(orgId);
  const { data: devices = [] } = useDevices(orgId);
  const { data: geofences = [] } = useGeofences(orgId);
  const { data: contacts = [] } = useContacts(orgId);
  const { data: organisations = [] } = useOrganisations();
  const { data: dashboards = [] } = useDashboards(orgId);
  const { data: reports = [] } = useReports(orgId);

  const handleOrganisationChange = useCallback(
    (newValue) => {
      if (newValue) {
        OrganisationService.setSelectedOrganisation(newValue);
        localStorage.setItem("selectedOrganisationID", newValue.id);
        DataService.refreshData(user.email, user.sub);
        if (window.location.href.includes("device")) {
          navigate("/");
        }
      }
    },
    [user, navigate]
  );

  const handleLogout = useCallback(() => {
    localStorage.removeItem("selectedOrganisationID");
    logout({ returnTo: window.location.origin });
  }, [logout]);

  const VISIBLE_COUNT = 5;

  const toggleShowAll = (key) =>
    setExpandedSubMenus((prev) => ({ ...prev, [key]: !prev[key] }));

  // Memoize menu styles with modern glassmorphic design
  const menuItemStyles = useMemo(
    () => ({
      button: ({ active, level }) => ({
        color: active
          ? theme.palette.primary.main
          : level === 1
            ? theme.palette.mode === "dark"
              ? theme.palette.grey[400]
              : theme.palette.grey[600]
            : theme.palette.mode === "dark"
              ? theme.palette.grey[300]
              : theme.palette.grey[700],
        backgroundColor: active
          ? theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.primary.main, 0.08)
          : "transparent",
        borderRadius:
          level === 1 ? "6px" : collapsed ? "10px" : "0 24px 24px 0",
        marginRight: collapsed ? "10px" : "12px",
        marginLeft: collapsed ? "10px" : level === 1 ? "4px" : "0",
        marginTop: level === 0 ? "2px" : "0px",
        marginBottom: level === 0 ? "2px" : "0px",
        padding: collapsed ? "10px" : undefined,
        paddingLeft: level === 1 ? "12px" : undefined,
        paddingTop: level === 1 ? "0px" : undefined,
        paddingBottom: level === 1 ? "0px" : undefined,
        justifyContent: collapsed ? "center" : undefined,
        position: "relative",
        transition: "color 0.2s ease, background-color 0.2s ease",
        fontWeight: level === 0 ? 500 : active ? 500 : 400,
        fontSize: level === 0 ? "0.875rem" : "0.8rem",
        height: level === 1 ? "32px" : "44px",
        // Active indicator bar - always present, only visible when active
        "&::before":
          !collapsed && level === 0
            ? {
                content: '""',
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                width: "3px",
                height: "60%",
                borderRadius: "0 4px 4px 0",
                background: active
                  ? `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                  : "transparent",
                transition: "background 0.2s ease",
              }
            : {},
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.primary.main, 0.15)
              : alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
        },
      }),
      icon: ({ active, level }) => ({
        color: active
          ? theme.palette.primary.main
          : theme.palette.mode === "dark"
            ? theme.palette.grey[400]
            : theme.palette.grey[600],
        transition: "color 0.2s ease",
        marginRight: collapsed ? 0 : undefined,
        fontSize: level === 1 ? "18px" : undefined,
      }),
      label: () => ({
        opacity: collapsed ? 0 : 1,
        transition: "opacity 0.2s ease",
      }),
      SubMenuExpandIcon: {
        display: "none",
      },
      subMenuContent: ({ open }) => ({
        backgroundColor: "transparent",
        marginLeft: open ? "30px" : 0,
        paddingTop: open ? "2px" : 0,
        paddingBottom: open ? "4px" : 0,
        borderLeft: open
          ? `1.5px solid ${alpha(
              theme.palette.divider,
              theme.palette.mode === "dark" ? 0.12 : 0.15
            )}`
          : "none",
      }),
    }),
    [theme.palette, collapsed]
  );

  // Memoize sidebar styles
  const sidebarRootStyles = useMemo(
    () => ({
      borderRightWidth: 0,
      "& .ps-submenu-content": {
        transition: "opacity 0.35s ease !important",
      },
      "& .ps-sidebar-container": {
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`
            : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#f5f5f5", 0.95)} 100%)`,
        backdropFilter: "blur(10px)",
        borderRight: `1px solid ${
          theme.palette.mode === "dark"
            ? alpha(theme.palette.divider, 0.1)
            : alpha(theme.palette.divider, 0.15)
        }`,
      },
    }),
    [theme.palette]
  );

  // Define menu items configuration
  const menuItems = useMemo(
    () => [
      { path: "/", icon: <Home />, label: "common.home" },
      {
        path: "/device",
        icon: <Router />,
        label: "common.devices",
        subItems: "devices",
      },
      {
        path: "/label",
        icon: <LabelOutlined />,
        label: "labels.index",
        subItems: "labels",
      },
      {
        path: "/action",
        icon: <AddAlarm />,
        label: "common.actions",
        subItems: "actions",
      },
      {
        path: "/geofence",
        icon: <Fence />,
        label: "common.geofences",
        subItems: "geofences",
      },
      {
        path: "/dashboard",
        icon: <Dashboard />,
        label: "common.dashboards",
        subItems: "dashboards",
      },
      {
        path: "/report",
        icon: <Description />,
        label: "common.reports",
        subItems: "reports",
      },
      {
        path: "/contact",
        icon: <Person />,
        label: "common.contacts",
        subItems: "contacts",
      },
      {
        path: "/organisation",
        icon: <CorporateFare />,
        label: "common.organisations",
        subItems: "organisations",
      },
    ],
    []
  );

  const subMenuData = {
    devices: {
      items: devices,
      getPath: (item) => `/device/${item.id}`,
      renderItem: (item) => item.name || item.dev_eui,
    },
    labels: {
      items: labels,
      getPath: (item) => `/label/${item.id}`,
      renderItem: (item) => (
        <Chip
          label={item.name}
          size="small"
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            backgroundColor: item.color
              ? alpha(item.color, 0.12)
              : alpha(theme.palette.action.selected, 0.08),
            color: item.color || "inherit",
            fontWeight: 600,
            borderRadius: "8px",
            fontSize: "0.75rem",
            height: 26,
            "& .MuiChip-label": { px: 1 },
          }}
        />
      ),
    },
    actions: {
      items: actions,
      getPath: (item) => `/action/${item.id}/logs`,
      renderItem: (item) => item.name,
    },
    geofences: {
      items: geofences,
      getPath: (item) => `/geofence/${item.id}`,
      renderItem: (item) => item.name,
    },
    contacts: {
      items: contacts,
      getPath: (item) => `/contact/${item.id}`,
      renderItem: (item) => item.name || item.email,
    },
    organisations: {
      items: organisations,
      getPath: (item) => `/organisation/${item.id}`,
      renderItem: (item) => item.name,
    },
    dashboards: {
      items: dashboards,
      getPath: (item) => `/dashboard/${item.id}`,
      renderItem: (item) => item.name,
    },
    reports: {
      items: reports,
      getPath: (item) => `/report/${item.id}`,
      renderItem: (item) => item.name,
    },
  };

  const renderSubItems = (menuItem) => {
    const config = subMenuData[menuItem.subItems];
    if (!config || !config.items.length) return null;

    const { items, getPath, renderItem } = config;
    const key = menuItem.subItems;
    const isExpanded = expandedSubMenus[key];
    const visible = isExpanded ? items : items.slice(0, VISIBLE_COUNT);
    const hasMore = items.length > VISIBLE_COUNT;

    return (
      <>
        {visible.map((item) => (
          <MenuItem
            key={`${key}-${item.id}`}
            onClick={() => navigate(getPath(item))}
            active={location.pathname === getPath(item)}
          >
            {renderItem(item)}
          </MenuItem>
        ))}
        {hasMore && (
          <Box sx={{ pl: 2.5, pr: 2, py: 0.5 }}>
            <ButtonBase
              onClick={(e) => {
                e.stopPropagation();
                toggleShowAll(key);
              }}
              sx={{
                width: "100%",
                justifyContent: "center",
                py: 0.5,
                borderRadius: "8px",
                fontSize: "0.72rem",
                color: theme.palette.grey[500],
                gap: 0.5,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.action.hover, 0.06),
                  color: theme.palette.primary.main,
                },
              }}
            >
              {isExpanded
                ? t("common.show_less")
                : `+${items.length - VISIBLE_COUNT} ${t("common.more")}`}
              <ExpandMore
                sx={{
                  fontSize: 14,
                  transition: "transform 0.2s ease",
                  transform: isExpanded ? "rotate(180deg)" : "none",
                }}
              />
            </ButtonBase>
          </Box>
        )}
      </>
    );
  };

  const toggleSubMenu = (key) =>
    setOpenSubMenus((prev) => {
      const next = { ...prev, [key]: !isSubMenuOpen(key) };
      localStorage.setItem("sidebarOpenSubMenus", JSON.stringify(next));
      return next;
    });

  const isSubMenuOpen = (key) =>
    openSubMenus[key] !== undefined ? openSubMenus[key] : true;

  return (
    <Sidebar
      collapsed={collapsed}
      width="280px"
      transitionDuration={0}
      className="app"
      rootStyles={sidebarRootStyles}
    >
      {/* Primary branded header bar */}
      <Box
        sx={{
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          px: collapsed ? 1 : 1.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 1,
          minHeight: 48,
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                : `linear-gradient(90deg, ${alpha("#fff", 0.3)}, ${alpha("#fff", 0.1)})`,
          },
        }}
      >
        {/* Hamburger / Collapse toggle */}
        <Box
          onClick={handleCollapsed}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 1.5,
            cursor: "pointer",
            flexShrink: 0,
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.1)
                : alpha("#fff", 0.15),
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.2)
                  : alpha("#fff", 0.25),
            },
          }}
        >
          <MenuRounded
            sx={{
              fontSize: 20,
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.primary.main
                  : "#fff",
            }}
          />
        </Box>

        {/* Site title (expanded only) */}
        {!collapsed && (
          <Link to="/" style={{ textDecoration: "none", minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.3px",
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.main
                    : "#fff",
                transition: "opacity 0.2s ease",
                lineHeight: 1.3,
                "&:hover": {
                  opacity: 0.85,
                },
              }}
            >
              {CONFIG.siteTitle}
            </Typography>
          </Link>
        )}

        {/* Right side: theme toggle + support (expanded only) */}
        {!collapsed && (
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.25,
            }}
          >
            <Tooltip
              title={
                themeMode === "dark"
                  ? t("common.switch_to_light_mode")
                  : t("common.switch_to_dark_mode")
              }
              placement="bottom"
            >
              <IconButton
                size="small"
                onClick={() => handleThemeChange()}
                sx={{
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.main
                      : alpha("#fff", 0.85),
                  borderRadius: "8px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.15)
                        : alpha("#fff", 0.2),
                  },
                }}
              >
                {themeMode === "dark" ? (
                  <Brightness7 sx={{ fontSize: 18 }} />
                ) : (
                  <Brightness4 sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>

            {CONFIG.show_support_link && CONFIG.support_url && (
              <Tooltip title={t("common.support")} placement="bottom">
                <IconButton
                  size="small"
                  onClick={() => window.open(CONFIG.support_url, "_blank")}
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.main
                        : alpha("#fff", 0.85),
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.primary.main, 0.15)
                          : alpha("#fff", 0.2),
                    },
                  }}
                >
                  <Support sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* User section */}
      <Box
        sx={{
          px: collapsed ? 1 : 1.5,
          pt: 1.5,
          pb: 0.5,
          display: "flex",
          flexDirection: "column",
          alignItems: collapsed ? "center" : "flex-start",
          gap: 0.75,
        }}
      >
        {/* Avatar + user info row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 1.25,
            width: "100%",
            py: 0.75,
          }}
        >
          <Avatar
            src={user?.picture}
            alt={user?.name}
            sx={{
              width: collapsed ? 32 : 36,
              height: collapsed ? 32 : 36,
              fontSize: collapsed ? 14 : 16,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              transition: "all 0.2s ease",
            }}
          >
            {!user?.picture && (
              <AccountCircle sx={{ fontSize: collapsed ? 20 : 24 }} />
            )}
          </Avatar>

          {!collapsed && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  lineHeight: 1.3,
                  fontSize: "0.82rem",
                }}
              >
                {user?.name || user?.nickname || ""}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.6),
                  fontSize: "0.7rem",
                  lineHeight: 1.3,
                  display: "block",
                }}
              >
                {user?.email || ""}
              </Typography>
            </Box>
          )}

          {/* Account settings + Logout (expanded only) */}
          {!collapsed && (
            <>
              <Tooltip title={t("common.account")} placement="top">
                <IconButton
                  size="small"
                  onClick={() => navigate("/account")}
                  sx={{
                    color: theme.palette.text.secondary,
                    borderRadius: "8px",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <Settings sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("common.logout")} placement="top">
                <IconButton
                  size="small"
                  onClick={handleLogout}
                  sx={{
                    color: theme.palette.text.secondary,
                    borderRadius: "8px",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      color: theme.palette.error.main,
                    },
                  }}
                >
                  <Logout sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        {/* Org selector */}
        {!collapsed && (
          <Autocomplete
            id="sidebar-org-select"
            size="small"
            options={organisations || []}
            getOptionLabel={(option) => option?.name || ""}
            getOptionKey={(option) => option?.id || ""}
            value={selectedOrganisation || null}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={(_e, newValue) => handleOrganisationChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("common.select_organisation")}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <CorporateFare
                          sx={{
                            fontSize: 18,
                            color: alpha(theme.palette.text.secondary, 0.5),
                            ml: 0.5,
                            mr: -0.5,
                          }}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                fontSize: "0.82rem",
                height: 36,
                transition: "all 0.2s ease",
                "& fieldset": {
                  borderColor: alpha(theme.palette.divider, 0.2),
                },
                "&:hover fieldset": {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                "&.Mui-focused": {
                  backgroundColor: theme.palette.background.paper,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
              "& .MuiOutlinedInput-input::placeholder": {
                color: alpha(theme.palette.text.secondary, 0.5),
                opacity: 1,
              },
            }}
          />
        )}
      </Box>

      {!collapsed && (
        <Box
          sx={{
            px: 1.5,
            pt: 0.5,
            pb: 1.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <GlobalSearch />
        </Box>
      )}
      <Menu menuItemStyles={menuItemStyles} transitionDuration={0}>
        {menuItems.map((item) => {
          const hasSubItems =
            item.subItems && subMenuData[item.subItems]?.items?.length > 0;

          return hasSubItems && !collapsed ? (
            <SubMenu
              key={item.path}
              icon={item.icon}
              label={t(item.label)}
              active={location.pathname.startsWith(item.path)}
              open={isSubMenuOpen(item.subItems)}
              onOpenChange={() => {}}
              onClick={() => navigate(item.path)}
              suffix={
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSubMenu(item.subItems);
                  }}
                  sx={{
                    p: 0.25,
                    color: theme.palette.grey[500],
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <ExpandMore
                    sx={{
                      fontSize: 18,
                      transition: "transform 0.2s ease",
                      transform: isSubMenuOpen(item.subItems)
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  />
                </IconButton>
              }
            >
              {renderSubItems(item)}
            </SubMenu>
          ) : (
            <MenuItem
              key={item.path}
              onClick={() => navigate(item.path)}
              icon={item.icon}
              active={location.pathname === item.path}
            >
              {t(item.label)}
            </MenuItem>
          );
        })}
      </Menu>
    </Sidebar>
  );
}

export default React.memo(SidebarLayout);
